import { useState } from 'react'
import { useTranslate } from 'react-polyglot'

import { GlobalSearchBarDetails, GlobalSearchBarType } from 'types/GlobalSearchBarDetails'

const useSupportLinks = (): GlobalSearchBarDetails[] => {
  const t = useTranslate()
  const COMMON_TAGS = [t('Doc')]
  const HOW_TO_TAGS = [t('How to')]

  const [supportLinks] = useState<GlobalSearchBarDetails[]>(
    (
      [
        {
          to: 'https://link.goto.com/add_voicemail_greeting',
          keyword: t('How do I add a voicemail greeting?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            ...[t('Add voicemail greeting'), t('Set up voicemail'), t('Voicemail setup')],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/change_caller_id',
          keyword: t('How do I change my caller ID?'),
          external: true,
          tags: [...COMMON_TAGS, ...HOW_TO_TAGS, [t('Change Caller ID'), t('CID'), t('Outbound phone number')]],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/route_phone_number',
          keyword: t('How do I route a phone number?'),
          external: true,
          tags: [...COMMON_TAGS, ...HOW_TO_TAGS, [t('Call routing'), t('Incoming calls')]],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/add_user',
          keyword: t('How do I add a user?'),
          external: true,
          tags: [...COMMON_TAGS, ...HOW_TO_TAGS, [t('Bulk add users'), t('Add multiple users'), t('Invite new users')]],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/settings_direct_extension',
          keyword: t('How do I change the settings on a direct extension?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Access recorded call'),
              t('Voicemail to email'),
              t('Voicemail password'),
              t('Voicemail transcripts'),
              t('Find me'),
              t('Fmfm'),
              t('Transcription'),
              t('Transfer to voicemail'),
              t('Reset voicemail'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/provision_device',
          keyword: t('How do I provision a device?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            t('Plug and play'),
            t('Activate phone'),
            t('One touch activation'),
            t('Manual provisioning'),
            t('Phone activation'),
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/add_extension_',
          keyword: t('How do I add an extension?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Add ring group'),
              t('Add conference bridge'),
              t('Add virtual fax'),
              t('Add shared voicemail'),
              t('Extension mapping'),
              t('Add a line'),
              t('Create a line'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/settings_virtual_fax',
          keyword: t('How do I change the settings on a virtual fax?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Email to fax'),
              t('Send fax'),
              t('Fax'),
              t('Email destination'),
              t('Fax storage'),
              t('Fax permissions'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/dial_plan_nodes',
          keyword: t('What do the dial plan nodes do?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            [
              t('Voicemail routing'),
              t('Detect fax'),
              t('Schedule'),
              t('Auto attendant'),
              t('Dial plan directory'),
              t('CRM integration'),
              t('Restrict caller access'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/different_extensions',
          keyword: t('What are the different types of extensions?'),
          external: true,
          tags: [...COMMON_TAGS, [t('Call flow')]],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/add_sound_clip_or_music_file',
          keyword: t('How do I add a sound clip or music file?'),
          external: true,
          tags: [...COMMON_TAGS, ...HOW_TO_TAGS, [t('Playlist'), t('Hold music')]],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/manage_phone_numbers',
          keyword: t('How do I manage my phone numbers?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Order a phone number'),
              t('Delete a phone number'),
              t('Cancel phone number'),
              t('Buy a new number'),
              t('Toll-free'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/settings_dial_plan',
          keyword: t('How do I change the settings on a dial plan?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Add dial plan'),
              t('Export dial plan'),
              t('Copy dial plan'),
              t('Delete dial plan'),
              t('Number of rings before voicemail'),
              t('Change main greeting'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/change_users_settings',
          keyword: t(`How do I change a user's settings?`),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('International dialing permissions'),
              t('Hot desking'),
              t('Change one user time zone'),
              t('Enable call parking permission'),
              t('Allow user to monitor other users'),
              t('Allow user to record or delete recorded prompts'),
              t('Allow long distance calls'),
              t('Allow toll-free calling'),
              t('Allow malicious area codes'),
              t('Allow only internal dialing'),
              t('Allow start meetings by phone'),
              t('Allow organizers to record meetings'),
              t('Allow text to email forwarding'),
              t('Allow remote control'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/manage_extensions',
          keyword: t('How do I manage my extensions?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Delete extension'),
              t('Delete dial plan'),
              t('Delete conference bridge'),
              t('Delete virtual fax'),
              t('Delete ring group'),
              t('Delete shared voicemail'),
              t('Delete line'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/manage_default_phone_settings',
          keyword: t('How do I manage my default phone settings?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Set up call recording'),
              t('Turn on email alerts for emergency calls'),
              t('Default emergency location'),
              t('Default time zone'),
              t('Default language voicemail'),
              t('Default area code'),
              t('Default caller ID'),
              t('Default hold music'),
              t('Number of extension digits'),
              t('Maximum voicemail length'),
              t('Default e911'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/settings_phone_number',
          keyword: t('How do I change the settings on a phone number?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Block caller id'),
              t('Route calls'),
              t('Choose hold music for a phone number'),
              t('Set up call recording for a phone number'),
              t('View phone number activity'),
              t('Set up SMS messaging'),
              t('Texting permission'),
              t('Enable chat queues'),
              t('Delete all texts'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/add_device',
          keyword: t('How do I add a device?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [
              t('Mac address'),
              t('Standalone device'),
              t('Device without user'),
              t('Add new phone hardware'),
              t('Add hardware'),
              t('Setup hardware'),
            ],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/add_emergency_location',
          keyword: t('How do I add an emergency location?'),
          external: true,
          tags: [...COMMON_TAGS, ...HOW_TO_TAGS, [t('E911')]],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/add_schedule',
          keyword: t('How do I add a schedule?'),
          external: true,
          tags: [...COMMON_TAGS, ...HOW_TO_TAGS, [t('Office hours'), t('Hours'), t('Holidays')]],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://link.goto.com/change_dial_plan',
          keyword: t('How do I make changes to a dial plan?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [t('Auto-attendant'), t('Auto response'), t('Update incoming call prompt'), t('Call queue')],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
        {
          to: 'https://support.goto.com/admin/help/register-your-brand-and-campaigns-using-sms-registration',
          keyword: t('How do I register for business SMS?'),
          external: true,
          tags: [
            ...COMMON_TAGS,
            ...HOW_TO_TAGS,
            [t('10DLC'), t('Brand'), t('Registration'), t('The Campaign registry')],
          ],
          linkType: GlobalSearchBarType.SUPPORT_ARTICLE,
        },
      ] as GlobalSearchBarDetails[]
    ).sort((keyword1, keyword2) => keyword1.keyword.localeCompare(keyword2.keyword)),
  )

  return supportLinks
}

export default useSupportLinks
